import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { embedSnippets, publicFormUrl } from '@jfh/intake-shared';
import { download, qrPng, qrSvg } from '../lib/qr.js';
import { openExternal } from '../lib/teams.js';
import { Modal } from './Modal.js';
import { Icon } from './Icon.js';
import { useToast } from './Toast.js';

/**
 * One Share dialog (F10 P4) for the Forms list, Share forms and the builder:
 * the link, the website snippets and the QR code, each a tab.
 */

export interface ShareTarget {
  name: string;
  slug: string;
  /** Published, not archived: the address serves the form. */
  live: boolean;
}

type Tab = 'link' | 'web' | 'qr';
const TABS: [Tab, string][] = [
  ['link', 'Link'],
  ['web', 'On the website'],
  ['qr', 'QR code'],
];

const codeStyle: CSSProperties = {
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border)',
  borderRadius: 7,
  padding: 12,
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-1)',
  overflow: 'auto',
  margin: '6px 0 0',
  lineHeight: 1.6,
  whiteSpace: 'pre',
};

function useCopy() {
  const toast = useToast();
  return (text: string, title: string) => {
    navigator.clipboard?.writeText(text);
    toast({ title, icon: 'copy' });
  };
}

function Snippet({ title, help, text, copied, children }: { title: string; help: string; text: string; copied: string; children?: ReactNode }) {
  const copy = useCopy();
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 13.5 }}>{title}</div>
      <div className="field-help" style={{ marginTop: 2 }}>
        {help}
      </div>
      {children}
      <pre style={codeStyle}>{text}</pre>
      <button className="btn sm" style={{ marginTop: 6 }} onClick={() => copy(text, copied)}>
        <Icon name="copy" size={14} /> Copy
      </button>
    </div>
  );
}

function LinkTab({ url }: { url: string }) {
  const copy = useCopy();
  return (
    <>
      <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-2)' }}>
        Send this to applicants, or put it in an email signature.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <code className="mono" style={{ ...codeStyle, margin: 0, flex: 1, minWidth: 240, padding: '8px 10px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          {url}
        </code>
        <button className="btn" onClick={() => copy(url, 'Link copied')}>
          <Icon name="copy" size={14} /> Copy
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn sm" onClick={() => openExternal(url)}>
          <Icon name="external" size={14} /> Open the form
        </button>
        <button className="btn sm" onClick={() => openExternal(`${url}?test=1`)}>
          <Icon name="bolt" size={14} /> Try it with sample answers
        </button>
      </div>
      <div className="field-help" style={{ marginTop: 8 }}>
        Sample answers are filled in for you. Sending it still creates a real application in Teams and iQ.
      </div>
    </>
  );
}

function WebTab({ target, baseUrl }: { target: ShareTarget; baseUrl: string }) {
  const [buttonText, setButtonText] = useState(target.name);
  useEffect(() => setButtonText(target.name), [target.name]);
  const s = embedSnippets(baseUrl, target.slug, target.name, buttonText.trim() || target.name);
  return (
    <>
      <Snippet
        title="Show the whole form on a page"
        help="Paste into an HTML embed on the Wix page. It grows to fit the form, and publishing a new version updates it everywhere."
        text={s.autoHeight}
        copied="Embed code copied"
      />
      <Snippet title="A button that opens the form" help="A JFH-green button that opens the form in a new tab." text={s.link} copied="Button code copied">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 8 }}>
          <span style={{ color: 'var(--text-2)', flex: 'none' }}>Button text</span>
          <input className="input" value={buttonText} maxLength={80} onChange={(e) => setButtonText(e.target.value)} style={{ flex: 1 }} />
        </label>
      </Snippet>
      <Snippet
        title="If the page removes scripts"
        help="For editors that strip scripts (such as nopCommerce pages). The form scrolls inside a fixed height; change the height to suit."
        text={s.fixedHeight}
        copied="Embed code copied"
      />
    </>
  );
}

function QrTab({ target, url }: { target: ShareTarget; url: string }) {
  const toast = useToast();
  const svg = useMemo(() => qrSvg(url), [url]);
  const file = `jfh-form-${target.slug}-qr`;
  const savePng = async () => {
    try {
      download(await qrPng(url), `${file}.png`);
    } catch (e) {
      toast({ title: 'Couldn’t make the PNG', body: e instanceof Error ? e.message : undefined, icon: 'x' });
    }
  };
  return (
    <div style={{ marginTop: 16, display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <div
        role="img"
        aria-label={`QR code for ${url}`}
        style={{ width: 168, height: 168, flex: 'none', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
        // Geometry only: qrSvg builds the markup from the matrix, no user text.
        dangerouslySetInnerHTML={{ __html: svg.replace('<svg ', '<svg width="100%" height="100%" ') }}
      />
      <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: 'var(--text-2)' }}>
        <div>
          Opens <span className="mono" style={{ color: 'var(--text-1)', overflowWrap: 'anywhere' }}>{url}</span>.
        </div>
        <div style={{ marginTop: 6 }}>
          PNG for documents and labels. SVG for printers, it stays sharp at any size. Scan it with a phone before you print.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn sm primary" onClick={() => void savePng()}>
            <Icon name="upload" size={14} /> PNG
          </button>
          <button className="btn sm" onClick={() => download(new Blob([svg], { type: 'image/svg+xml' }), `${file}.svg`)}>
            <Icon name="upload" size={14} /> SVG
          </button>
        </div>
      </div>
    </div>
  );
}

export function ShareDialog({
  target,
  baseUrl,
  onClose,
}: {
  target: ShareTarget | null;
  baseUrl: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('link');
  const openSlug = target?.slug ?? null;
  // Back to the Link tab each time the dialog opens (callers rebuild `target` every render).
  useEffect(() => {
    if (openSlug !== null) setTab('link');
  }, [openSlug]);
  const url = target ? publicFormUrl(baseUrl, target.slug) : '';

  return (
    <Modal open={target !== null} onClose={onClose} width={600}>
      {target && (
        <div style={{ padding: '22px 24px' }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Share {target.name}</h3>
          {!target.live && (
            <div className="field-help" style={{ marginTop: 6, color: 'var(--st-declined)' }}>
              Not published yet: this address shows “Form not found” until you publish it.
            </div>
          )}
          <div
            role="tablist"
            style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 8, marginTop: 14, width: 'fit-content' }}
          >
            {TABS.map(([k, l]) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                style={{
                  border: 'none',
                  background: tab === k ? 'var(--bg-surface)' : 'transparent',
                  color: tab === k ? 'var(--text-1)' : 'var(--text-2)',
                  fontWeight: 700,
                  fontSize: 13,
                  padding: '6px 14px',
                  borderRadius: 6,
                  boxShadow: tab === k ? 'var(--sh-xs)' : 'none',
                  cursor: 'pointer',
                }}
              >
                {l}
              </button>
            ))}
          </div>
          {tab === 'link' && <LinkTab url={url} />}
          {tab === 'web' && <WebTab target={target} baseUrl={baseUrl} />}
          {tab === 'qr' && <QrTab target={target} url={url} />}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
